import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

type GalleryTab = 'all' | 'apartment' | 'community';

function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | null {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return null;
}

const TABS: { key: GalleryTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'apartment', label: 'Apartment' },
  { key: 'community', label: 'Community' },
];

export function Gallery() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<GalleryTab>('all');

  const images = useCollectionBinding<GalleryImage>('gallery.images', {
    collection: 'photo_gallery',
  });

  const galleryImages = images && images.length > 0 ? images : [];

  const classified = useMemo(() => {
    const map = new Map<GalleryImage, 'apartment' | 'community' | null>();
    for (const img of galleryImages) {
      map.set(img, classifyPhoto(img));
    }
    return map;
  }, [galleryImages]);

  const filteredImages = useMemo(() => {
    if (activeTab === 'all') return galleryImages;
    return galleryImages.filter((img) => classified.get(img) === activeTab);
  }, [galleryImages, classified, activeTab]);

  const tabCounts = useMemo(() => {
    let apt = 0;
    let com = 0;
    for (const cat of classified.values()) {
      if (cat === 'apartment') apt++;
      else if (cat === 'community') com++;
    }
    return { all: galleryImages.length, apartment: apt, community: com };
  }, [classified, galleryImages.length]);

  const lightboxOpen = selectedIndex !== null;
  const selectedImage = lightboxOpen ? filteredImages[selectedIndex] : null;

  const goToPrev = useCallback(() => {
    if (selectedIndex === null || filteredImages.length <= 1) return;
    setSelectedIndex((selectedIndex - 1 + filteredImages.length) % filteredImages.length);
  }, [selectedIndex, filteredImages.length]);

  const goToNext = useCallback(() => {
    if (selectedIndex === null || filteredImages.length <= 1) return;
    setSelectedIndex((selectedIndex + 1) % filteredImages.length);
  }, [selectedIndex, filteredImages.length]);

  const closeLightbox = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  // Mobile swipe support — track touch start/end X to navigate the lightbox
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      const SWIPE_THRESHOLD = 50;
      if (deltaX < -SWIPE_THRESHOLD) goToNext();
      else if (deltaX > SWIPE_THRESHOLD) goToPrev();
    },
    [goToNext, goToPrev],
  );

  // Keyboard navigation: Escape to close, Arrow keys for prev/next
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, goToPrev, goToNext]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  return (
    <section className="py-8 md:py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tab filters */}
        {galleryImages.length > 0 && (
          <div className="gallery-tabs">
            {TABS.map((tab) => {
              const count = tabCounts[tab.key];
              if (tab.key !== 'all' && count === 0) return null;
              return (
                <button
                  key={tab.key}
                  className={`gallery-tab${activeTab === tab.key ? ' gallery-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Photo grid */}
        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {filteredImages.map((img, index) => (
              <div
                key={`${activeTab}-${index}`}
                className="aspect-square overflow-hidden group cursor-pointer gallery-item"
                onClick={() => setSelectedIndex(index)}
              >
                <VibesitesImage
                  url={img.image}
                  alt={img.alt_text || img.title || ''}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Gallery images will be displayed here.</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal — fixed-size container so all images display consistently */}
      {lightboxOpen && selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          {/* Close button — consistent SVG X icon used across theme (matches VirtualTourEmbed) */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors duration-200"
            aria-label="Close lightbox"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image counter */}
          {filteredImages.length > 1 && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 z-20 text-white/80 text-sm font-body tracking-wide">
              {selectedIndex + 1} / {filteredImages.length}
            </div>
          )}

          {/* Previous button — consistent chevron icon used in PropertyOverview carousel */}
          {filteredImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
              aria-label="Previous image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}

          {/* Next button — consistent chevron icon used in PropertyOverview carousel */}
          {filteredImages.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] hover:bg-white transition-all duration-200"
              aria-label="Next image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          )}

          {/* Fixed-size image container — consistent dimensions regardless of image aspect ratio */}
          <div
            className="relative w-[90vw] h-[80vh] max-w-5xl flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <VibesitesImage
              url={selectedImage.image}
              alt={selectedImage.alt_text || selectedImage.title || ''}
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-sm"
            />
          </div>
        </div>
      )}

      <style>{`
        .gallery-tabs {
          display: flex;
          justify-content: center;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
        }

        .gallery-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          padding: 0.5rem 1.25rem;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .gallery-tab:first-child {
          border-radius: 3px 0 0 3px;
        }
        .gallery-tab:last-child {
          border-radius: 0 3px 3px 0;
        }
        .gallery-tab:hover {
          color: var(--vibe-color-text);
          border-color: var(--vibe-color-text-muted);
        }
        .gallery-tab--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        .gallery-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-primary);
        }

        .gallery-item {
          animation: gallery-fade-in 0.35s ease both;
        }

        @keyframes gallery-fade-in {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-item { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Gallery',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'gallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
    },
  },
};

(Gallery as any).contentSchema = contentSchema;

export default Gallery;
